import { z } from "zod";
import { CartItem } from "./cart.js";
import { PillowShapeSchema } from "./enums.js";
import { PROJECT_CONFIG_SIZES, ProjectSizeCm } from "./project-config.js";

// Shape of `orders.delivery` (jsonb) and the `delivery` field of a checkout
// request. The buyer's InPost Delivery choice — collected on OUR checkout page,
// never on Stripe (ADR-0020 rev. 2026-07-08) — as a discriminated union on
// `method`: a `paczkomat` (locker point code) or a `courier` (full PL address).
// Both carry a receiver name + a REQUIRED phone (InPost SMSes the locker code).
// The buyer's email already lives on the `user`, so it is not repeated here.
// Declared before `CheckoutCreateRequest` because that request embeds it.
// PL post code `XX-XXX` (e.g. `31-021`) — the only format InPost courier accepts
// for a domestic address. Exported so the checkout page's button gate mirrors the
// exact server rule (no drift between the two, #257).
export const PL_POST_CODE_RE = /^\d{2}-\d{3}$/;

// A permissive receiver-phone sanity guard: a leading `+` or digit, then digits /
// spaces / `()` / `-`, at least 7 characters overall. NOT a strict validator (InPost
// re-validates on label creation) — just enough to reject junk like "x" that would
// silently break the SMS pickup-code delivery, while accepting `600100200` and
// `+48 600 100 200`. Shared so the checkout page gate and the server agree (#257).
export const PHONE_RE = /^[+\d][\d\s()-]{6,}$/;

// Upper length bounds on the free-text delivery fields. The `delivery` jsonb is
// otherwise unbounded, so a client could persist arbitrarily large strings into
// `orders.delivery` (mirrors the `.max()` convention on `AdminOrderNoteRequest`
// and the settings text fields). Generous enough for real names/addresses; tight
// enough that no single order row can be bloated (#257 hardening).
const NAME_MAX = 100;
const PHONE_MAX = 30;
const POINT_CODE_MAX = 64;
const POINT_NAME_MAX = 200;
const POINT_ADDRESS_MAX = 300;
const STREET_MAX = 120;
const BUILDING_MAX = 30;
const CITY_MAX = 100;

const DeliveryReceiver = z.object({
  // Whom the parcel is addressed to on the InPost label — required for both
  // methods. `.trim()` runs before `.min(1)`, so a whitespace-only value fails;
  // `.max()` bounds the persisted string.
  name: z.string().trim().min(1).max(NAME_MAX),
  // Required for both methods: InPost delivers the paczkomat pickup code by SMS
  // and the courier calls on delivery, so a phone is never optional. Bounded +
  // sanity-checked against junk (see `PHONE_RE`); InPost is the strict validator.
  phone: z.string().trim().max(PHONE_MAX).regex(PHONE_RE, "Podaj poprawny numer telefonu"),
});

export const OrderDelivery = z.discriminatedUnion("method", [
  z.object({
    method: z.literal("paczkomat"),
    point: z.object({
      // The locker point code (e.g. `KRA010`) — the authoritative identifier
      // (CONTEXT.md Paczkomat). Non-empty is the only hard requirement in this
      // slice; the plain text input is replaced by the Geowidget map in #256.
      code: z.string().trim().min(1).max(POINT_CODE_MAX),
      // Human-readable point name + address, filled by the locker-picker map
      // (#256). Optional here because the no-map text input only has the code;
      // when present they are snapshotted for the email + admin display.
      name: z.string().trim().min(1).max(POINT_NAME_MAX).optional(),
      address: z.string().trim().min(1).max(POINT_ADDRESS_MAX).optional(),
    }),
    receiver: DeliveryReceiver,
  }),
  z.object({
    method: z.literal("courier"),
    address: z.object({
      street: z.string().trim().min(1).max(STREET_MAX),
      buildingNumber: z.string().trim().min(1).max(BUILDING_MAX),
      city: z.string().trim().min(1).max(CITY_MAX),
      // PL post code `XX-XXX` (e.g. `31-021`) — the only format InPost courier
      // accepts for a domestic address (`PL_POST_CODE_RE`, shared with the page).
      postCode: z.string().trim().regex(PL_POST_CODE_RE, "Kod pocztowy musi mieć format XX-XXX"),
      // Single-carrier, PL-only MVP (ADR-0021 rev.): the country is fixed to PL,
      // not a free field, so a foreign address can never reach the operator.
      countryCode: z.literal("PL"),
    }),
    receiver: DeliveryReceiver,
  }),
]);
export type OrderDelivery = z.infer<typeof OrderDelivery>;

// The buyer-facing PL brand name of each delivery method (CONTEXT.md Paczkomat /
// Kurier InPost). ONE source of truth consumed by the confirmation + owner emails,
// the Stripe shipping line item, and the admin order detail — so a rename can never
// leave the receipt, the email, and the console showing three different names
// (#257 consolidation). Co-located with `OrderDelivery`; the storefront checkout
// keeps its own i18n keys (it is the one multi-language surface).
export const DELIVERY_METHOD_LABEL_PL: Record<OrderDelivery["method"], string> = {
  paczkomat: "Paczkomat InPost",
  courier: "Kurier InPost",
};

// Resolve the flat shipping cost (cents) for a chosen delivery method from the
// operator-editable per-method prices (`shipping_cost_paczkomat_cents` /
// `shipping_cost_courier_cents`, #240). The client never sends a price — the
// checkout service computes it here exactly as it resolves the product price
// from the variant. A method zeroed by the operator ⇒ free shipping (0).
export function shippingCentsFor(
  method: OrderDelivery["method"],
  costs: { paczkomatCents: number; courierCents: number },
): number {
  return method === "paczkomat" ? costs.paczkomatCents : costs.courierCents;
}

// Request of `POST /checkout` (#224 / #225 / #255; multi-line ADR-0054 §3): the
// **cart** — a list of `{ productId, quantity }` — plus the buyer data collected on
// our checkout page (ADR-0048 guest funnel). The server re-verifies ownership of
// every product against the caller's anonymous session and re-prices each line from
// its CURRENT variant (`resolveVariant`), and `delivery` resolves the one flat
// shipping cost — the client never sends a price. The InPost Delivery (method +
// destination + receiver) is captured HERE, not on Stripe (ADR-0020 rev.). One
// order + N `order_lines` + one Stripe session (N product line items + 1 shipping).
export const CheckoutCreateRequest = z.object({
  // The cart lines to buy (ADR-0054 §2): at least one `{ productId, quantity }`.
  // Each product's ownership + price is re-checked server-side (never trusted).
  items: z.array(CartItem).min(1),
  // Buyer email captured at checkout. Trimmed + lowercased before it is
  // validated so re-using an address upserts the *same* lightweight `user`
  // (no duplicate rows, no account takeover — ADR-0048); obviously malformed
  // addresses are rejected here at the edge.
  email: z.string().trim().toLowerCase().pipe(z.email()),
  // Art. 38 pkt 3 withdrawal-waiver (ADR-0020): a SEPARATE, unticked checkbox the
  // buyer must actively tick before paying. `true` is the only accepted value, so
  // an unacknowledged waiver fails validation — it must never be bundled into
  // general T&C. All launch products are custom, so ONE waiver covers the order.
  withdrawalWaiverAcknowledged: z.literal(true),
  // The InPost Delivery choice collected on our checkout page (#255): method +
  // destination + receiver. The client never sends a price — the service reads
  // the flat per-method shipping cost from `settings` (`shippingCentsFor`) and
  // adds it to the total, exactly as it resolves the product prices. Persisted to
  // `orders.delivery`; carried to Stripe as one shipping line item (ADR-0020 rev.).
  delivery: OrderDelivery,
});
export type CheckoutCreateRequest = z.infer<typeof CheckoutCreateRequest>;

// Response of `POST /checkout` (ADR-0048 guest checkout, tracer bullet #223).
// The API creates the order + a Stripe Checkout Session and returns the hosted
// page URL; the browser redirects to it. Kept minimal on purpose — the buyer
// data (email / address / waiver) and line detail thicken in later slices.
export const CheckoutCreateResponse = z.object({
  url: z.string().url(),
});
export type CheckoutCreateResponse = z.infer<typeof CheckoutCreateResponse>;

// Response of `GET /checkout/:projectId` (#224 / #228): the variant + price a
// project resolves to, for display before paying (and re-read after the buyer
// changes size). A read-only projection of the resolved `product_variants` row —
// no order is created. `size_cm` is the nominal producer dimension (a single
// int today; a non-square 40×60 carries a nominal 60 — schema debt, ADR-0048).
export const CheckoutQuote = z.object({
  shape: PillowShapeSchema,
  sizeCode: z.enum(PROJECT_CONFIG_SIZES),
  sizeCm: z.number().int().positive(),
  // The exact printed face dimensions to show on checkout (#228): the cm the
  // buyer committed on the preview (`project.config.size_cm`), or a square
  // fallback from the variant's nominal `sizeCm` for a project that never
  // committed one. Read-only on checkout — size is chosen on the preview.
  widthCm: z.number().int().positive(),
  heightCm: z.number().int().positive(),
  variantId: z.guid(),
  // The project's pillow **Product** id (ADR-0054): the handle the browser-local
  // Cart references. Populated on the "Zamów" commit (which creates the Product) and
  // on a read of a project that already has one; NULL for a project that has no
  // Product yet (a GET before any commit). The checkout page uses it to ensure the
  // URL's project is represented in the cart when resuming (ADR-0051) to `/checkout`.
  productId: z.guid().nullable(),
  unitPriceCents: z.number().int().nonnegative(),
  currency: z.string(),
  // The rendered pillow for the checkout summary (#228): a freshly signed URL of
  // the project's `thumbnail` asset. Null when the project has no thumbnail yet
  // (a stale/failed job) — the checkout page falls back to a quiet placeholder.
  thumbnailUrl: z.string().url().nullable(),
  // Whether checkout can proceed right now (#228): mirrors the `payments_disabled`
  // kill-switch (ADR-0031) so the checkout page can disable Pay up-front with a
  // clear message instead of only failing on the POST. `false` ⇒ payments off.
  paymentsEnabled: z.boolean(),
  // The current operator-editable ceiling on quantity (`checkout_max_quantity`,
  // #240), read through the settings reader server-side and surfaced here so the
  // quantity stepper enforces the live bound with no deploy. The server also
  // rejects an over-max POST (defense against a stale quote / direct API call).
  maxQuantity: z.number().int().positive(),
  // Both delivery methods' flat shipping prices, read from `settings` (#255), so
  // the checkout page shows each method's price and recomputes the total live as
  // the buyer toggles — with no round-trip. The server still recomputes shipping
  // authoritatively on POST from the SAME settings; this is display only.
  shipping: z.object({
    paczkomatCents: z.number().int().nonnegative(),
    courierCents: z.number().int().nonnegative(),
    currency: z.string(),
  }),
});
export type CheckoutQuote = z.infer<typeof CheckoutQuote>;

// Body of `PATCH /checkout/:projectId/selection` (#228): the geometry the buyer
// committed on the preview's "Zamów", persisted before the checkout page loads.
// `shape` + `size` (the pricing tier, derived client-side from the slider /
// rectangle pick) drive `resolveVariant`; `dimensionsCm` is the exact footprint
// shown read-only on checkout and printed by the producer. The server persists
// all three to `project.config` and returns a fresh `CheckoutQuote`.
// Ownership-guarded to the session that owns the project (ADR-0048 guest funnel).
export const CheckoutSelectionRequest = z.object({
  shape: PillowShapeSchema,
  size: z.enum(PROJECT_CONFIG_SIZES),
  dimensionsCm: ProjectSizeCm,
});
export type CheckoutSelectionRequest = z.infer<typeof CheckoutSelectionRequest>;

// Response of `GET /checkout/:projectId/tier-pricing` (#228; generalized 2026-07-22
// for the always-visible configurator price). The catalogue base prices the project's
// fabric resolves to, per shape → size code, so the preview can show the ABSOLUTE
// price of the currently-configured pillow. The buyer picks shape + size BEFORE
// committing, so BOTH shapes' prices are returned and indexed client-side by
// `readout.tier`; no refetch on a shape toggle. This satisfies art. 4 ustawy o cenach
// (informowanie o cenach): the price must be shown where the product is offered, not
// only at checkout. Computed from the seeded `product_variants` rows (admin-editable),
// so it always tracks the real catalogue. Prices are GROSS (VAT-inclusive, B2C).
// Read-only; creates no order.
export const VariantPricing = z.object({
  pricesCents: z.object({
    // Silhouette is a 2-tier slider (S / L); the rectangle offers three discrete
    // sizes (S / M / L). All grosze, gross.
    silhouette: z.object({
      S: z.number().int().nonnegative(),
      L: z.number().int().nonnegative(),
    }),
    rectangle: z.object({
      S: z.number().int().nonnegative(),
      M: z.number().int().nonnegative(),
      L: z.number().int().nonnegative(),
    }),
  }),
  currency: z.string(),
  // The operator-editable shipping-box dimensions (cm), each `[longer, shorter]`, read
  // from the `silhouette_max_long_cm` / `_max_short_cm` / `_small_long_cm` /
  // `_small_short_cm` settings (#245, extended 2026-07-12). SILHOUETTE-slider only: its
  // cm range comes from the LARGE box, the S/L threshold line from the SMALL box.
  silhouetteBoxes: z.object({
    maxLongCm: z.number().int().positive(),
    maxShortCm: z.number().int().positive(),
    smallLongCm: z.number().int().positive(),
    smallShortCm: z.number().int().positive(),
  }),
});
export type VariantPricing = z.infer<typeof VariantPricing>;
